import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { useMicrosoftGraph } from '../../../contexts/MicrosoftGraphContext';
import { FileProps, FolderProps } from './types';
import { RootState, useAppDispatch } from '../../../store';
import { editDocument, deleteDocument, fetchFileDetails, updateSharedWithForFile, streamFile } from '../../../store/actions/DocumentsAction';
import { signDocument } from '../../../store/actions/documentSignActions';
import PDFViewer from './PDFViewer';
import SecureFileViewer from './SecureFileViewer';
import FileNavigator from './FileNavigator';
import DetailFrame, { CollaboratorBadge } from '../../shared/Frames/DetailFrame';
import { useSelector } from 'react-redux';
import { hideModal, showModal } from '../../../store/actions/modalActions';
import { useTranslation } from 'react-i18next';
import { FiLock } from 'react-icons/fi';
import { isBetaEnv } from '../../../utils/authUtils';
import { PiTrashLight, PiArrowsOutLight, PiClockBold, PiCloudArrowDownLight, PiClockCounterClockwiseLight, PiFilePdfLight, PiFileDocLight, PiInfoLight } from 'react-icons/pi';
import ViewerZoomControl, { readStoredZoom, writeStoredZoom } from '../../shared/viewer/ViewerZoomControl';
import { buildFileNavigationPath } from '../../../utils/fileNavigationUtils';
import { formatDocumentName } from '../../../utils/functions';
import FileTypeSkeleton from './FileTypeSkeleton';
import FileAuditLog from '../FileAuditLog';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, fileViewerTourSteps } from '../../shared/FeatureTour';
import ActivityDrawer from '../../shared/activity/ActivityDrawer';

interface EditorProps {
  file: string;
  folderID?: string;
  parentFolderID?: string;
  onClose: () => void;
  setEditorOpen: (value: boolean) => void;
  onFileDeleted: () => void;
  subscriptionActive?: boolean;
  allFolderFiles?: FileProps[];
}

const Editor: React.FC<EditorProps> = ({
  file,
  folderID,
  parentFolderID,
  onClose,
  setEditorOpen,
  onFileDeleted,
  subscriptionActive,
  allFolderFiles = []
}) => {
  const dispatch = useAppDispatch();
  const { isSignedIn, MSALsignIn, uploadFileToOneDrive, getEditLink, downloadFileFromOneDrive } = useMicrosoftGraph();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isPdf, setIsPDF] = useState(false);
  const [isDocSigned, setIsDocSigned] = useState(false);
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const [fileData, setFileData] = useState<FileProps | null>(null);
  const [isDocSignatory, setIsDocSignatory] = useState(false);
  const { t } = useTranslation();
  const [shareFile, setShareFile] = useState<any>(null);
  const [currentFileId, setCurrentFileId] = useState(file);
  const [currentFolderId, setCurrentFolderId] = useState(folderID || '');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showAuditLog, setShowAuditLog] = useState(false);
  // Reading zoom (files-3) - remembered, so it survives switching documents.
  const [zoom, setZoom] = useState<number>(() => readStoredZoom());
  const folderStructure = useSelector((state: RootState) => state.folders.structure || []);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'file-viewer',
    steps: fileViewerTourSteps,
    autoStartDelay: 800,
  });

  // Get subfolders for FileNavigator
  const currentFolderSubFolders = useMemo(() => {
    const findFolder = (folders: FolderProps[]): FolderProps | null => {
      for (const folder of folders) {
        if (folder.FolderID === currentFolderId) {
          return folder;
        }
        if (folder.SubFolders) {
          const found = findFolder(folder.SubFolders);
          if (found) return found;
        }
      }
      return null;
    };
    return findFolder(folderStructure)?.SubFolders || [];
  }, [currentFolderId, folderStructure]);

  const getFileDetails = useCallback(async (fileId: string = file, folderId: string | undefined = folderID, parentId: string | undefined = parentFolderID) => {
    setIsLoading(true);
    try {
      const fileData = await dispatch(fetchFileDetails(fileId, folderId, parentId));

      if (!fileData) {
        console.error('Failed to fetch file data');
        setIsLoading(false);
        return;
      }

      if (fileData?.Signers?.some((signer: any) => signer.UserID === userInfo.UserID)) {
        setIsDocSigned(true);
      }
      if (fileData?.Signatories?.some((signer: any) => signer.UserID === userInfo.UserID)) {
        setIsDocSignatory(true);
      }
      fileData?.SharedWith?.some((signer: any) => {
        if (signer.UserID === userInfo.UserID) {
          setShareFile(signer);
          return null;
        }
        return null;
      });

      const mimeType = fileData.FileType || 'application/octet-stream';

      // Stream file from backend with server-side decryption
      try {
        const streamResult = await dispatch(streamFile(fileId, fileData.FileName, 'inline', folderId, parentId, fileData.FileType) as any);

        if (!streamResult.success || !streamResult.blob) {
          console.error('Failed to stream file:', streamResult.error);
          setIsLoading(false);
          return;
        }

        const blobUrl = URL.createObjectURL(streamResult.blob);

        setFileData({
          ...fileData,
          decryptedBlobUrl: blobUrl,
          // Keep original FileType (document MIME type) - don't overwrite with
          // stream content type (e.g. text/html for DOCX→HTML conversion)
        });

        if ((streamResult.contentType || mimeType).includes('pdf') || fileData.FileName.toLowerCase().endsWith('.pdf')) {
          setIsPDF(true);
        }

      
      } catch (streamError) {
        console.error('Streaming error:', streamError);
        setIsLoading(false);
        return;
      }

      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching file details:', error);
      setIsLoading(false);
    }
  }, [dispatch, userInfo.UserID, file, folderID, parentFolderID]);

  useEffect(() => {
    getFileDetails(currentFileId, folderID, parentFolderID);
  }, [currentFileId, folderID, parentFolderID, getFileDetails]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (fileData?.decryptedBlobUrl) {
        URL.revokeObjectURL(fileData.decryptedBlobUrl);
      }
    };
  }, [fileData?.decryptedBlobUrl]);

  const openEditor = async () => {
    if (!isSignedIn) {
      await MSALsignIn();
      return;
    }
    if (!fileData || !fileData.decryptedBlobUrl) {
      console.error('File blob URL is undefined or empty.');
      return;
    }
    const response = await fetch(fileData.decryptedBlobUrl);
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error fetching file:', errorText);
    }
    const fileContent = await response.blob();
    if (fileContent.type === 'text/html') {
      const errorText = await fileContent.text();
      console.error('Received HTML instead of file content:', errorText);
      throw new Error('Fetched content is not a valid file.');
    }
    const validWordBlob = new Blob([fileContent], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });
    const fileName = fileData.FileName.endsWith('.docx') ? fileData.FileName : `${fileData.FileName}.docx`;
    const { fileID, updatedSharedWith, webURL } = await uploadFileToOneDrive(fileName, validWordBlob, fileData.SharedWith);
    let editLink = webURL || (await getEditLink(fileID));
    const editorWindow = window.open(editLink, '_blank');
    if (updatedSharedWith) {
      await dispatch(updateSharedWithForFile(fileData.FileID, updatedSharedWith));
    }
    if (!editorWindow) {
      console.error('Failed to open the editor window.');
      return;
    }
    const interval = setInterval(() => {
      if (editorWindow.closed) {
        clearInterval(interval);
        handleSave(fileID);
      }
    }, 1000);
  };

  const handleSave = async (uploadedFileID: string) => {
    try {
      setIsSaving(true);
      const updatedFileContent = await downloadFileFromOneDrive(uploadedFileID);
      if (!fileData) return;
      await dispatch(editDocument(fileData.FileID, fileData.FileName, updatedFileContent));
      await getFileDetails(currentFileId, folderID, parentFolderID);
    } catch (error) {
      console.error('Error saving file:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await dispatch(deleteDocument(currentFileId, () => onFileDeleted(), folderID));
      setEditorOpen(false);
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };

  /**
   * Build a contextual download name: "Context - Filename"
   * If the file is within a matter folder, prefix with matter title.
   * If within a client folder, prefix with client folder name.
   */
  const getDownloadName = useCallback((extension?: string) => {
    if (!fileData) return '';
    const baseName = fileData.FileName;
    const nameWithoutExt = baseName.replace(/\.[^.]+$/, '');
    const ext = extension || (baseName.match(/\.[^.]+$/)?.[0] ?? '');

    // Determine context prefix from ParentChain or MatterInfo
    let prefix = '';
    if (fileData.MatterInfo?.title) {
      prefix = fileData.MatterInfo.title;
    } else if (fileData.ParentChain && fileData.ParentChain.length > 0) {
      // Check if any ancestor is "Matters" or "Clients" - use the name after it
      const chain = fileData.ParentChain;
      for (let i = 0; i < chain.length; i++) {
        const folderName = chain[i].name?.toLowerCase();
        if ((folderName === 'matters' || folderName === 'clients') && i + 1 < chain.length) {
          prefix = chain[i + 1].name;
          break;
        }
      }
    }

    const displayName = prefix ? `${prefix} - ${nameWithoutExt}` : nameWithoutExt;
    return `${displayName}${ext}`;
  }, [fileData]);

  const handleDownload = useCallback(async () => {
    if (!fileData) return;
    try {
      const result = await dispatch(
        streamFile(currentFileId, fileData.FileName, 'attachment', currentFolderId || folderID, parentFolderID, fileData.FileType) as any
      );
      if (result.success && result.blob) {
        const url = URL.createObjectURL(result.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = getDownloadName();
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Download failed:', err);
    }
  }, [dispatch, fileData, currentFileId, currentFolderId, folderID, parentFolderID, getDownloadName]);

  const handleDownloadAsPdf = useCallback(async () => {
    if (!fileData) return;
    try {
      const result = await dispatch(
        streamFile(currentFileId, fileData.FileName, 'attachment', currentFolderId || folderID, parentFolderID, fileData.FileType, 'pdf') as any
      );
      if (result.success && result.blob) {
        const url = URL.createObjectURL(result.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = getDownloadName('.pdf');
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('PDF download failed:', err);
    }
  }, [dispatch, fileData, currentFileId, currentFolderId, folderID, parentFolderID, getDownloadName]);

  const handleSignDocument = async () => {
    dispatch(
      showModal({
        type: 'sign_doc',
        message: t("You are gonna sign this document which won't be reverted back. Please ensure to check your signatures before you proceed."),
        onConfirm: async (fileID: string) => {
          await dispatch(signDocument(fileID, async () => {
            await getFileDetails(currentFileId, folderID, parentFolderID);
          }));
          dispatch(hideModal());
        },
        onCancel: () => { },
        showModal: true,
        fileID: currentFileId,
      })
    );
  };

  /*
    THE WAY OUT OF A DOCUMENT (files-2).

    Each folder crumb links to the folder's REAL url - `buildFileNavigationPath`,
    the same builder the explorer navigates with. They used to be
    `/files?folder=<id>`, a shape nothing in the app parses, so a crumb landed
    the reader at the root of Files rather than in the folder they came from.
    Dropping the file id from the path is what closes the viewer: both Files
    surfaces watch the url and clear the editor when it no longer names a file.
  */
  const getBreadcrumbs = (): Array<{ name: string; href: string; current: boolean }> => {
    const breadcrumbs: Array<{ name: string; href: string; current: boolean }> = [
      { name: t('Files'), href: '/files', current: false }
    ];

    // Add folder path
    if (folderID && folderStructure.length > 0) {
      const findFolderPath = (folderId: string, folders: any[]): any[] => {
        for (const folder of folders) {
          if (folder.FolderID === folderId) {
            return [folder];
          }
          if (folder.SubFolders) {
            const path = findFolderPath(folderId, folder.SubFolders);
            if (path.length > 0) {
              return [folder, ...path];
            }
          }
        }
        return [];
      };

      const folderPath = findFolderPath(folderID, folderStructure);
      folderPath.forEach((folder, idx) => {
        breadcrumbs.push({
          name: folder.FolderName,
          href: buildFileNavigationPath(
            folder.FolderID,
            idx > 0 ? folderPath[idx - 1].FolderID : undefined
          ),
          current: idx === folderPath.length - 1 && !fileData
        });
      });
    }

    // Add current file
    if (fileData) {
      const { name: formattedName } = formatDocumentName(fileData.FileName);
      breadcrumbs.push({
        name: formattedName,
        href: '',
        current: true
      });
    }

    return breadcrumbs;
  };

  // Detect if this file is inside a Matters folder hierarchy
  const isMatterFile = !!fileData?.ParentChain?.some(
    (p: any) => (p.name || p.FolderName || '').toLowerCase() === 'matters'
  );

  // Build actions for dropdown
  const getActionItems = () => {
    const items: any[] = [];

    // Detect if file is DOCX
    const lowerName = (fileData?.FileName || '').toLowerCase();
    const lowerMime = (fileData?.FileType || '').toLowerCase();
    const isDocx = lowerName.endsWith('.docx') || lowerName.endsWith('.doc')
      || lowerMime.includes('wordprocessingml') || lowerMime.includes('msword');

    if (isDocx) {
      items.push(
        {
          id: 'download-docx',
          label: t('Download as DOCX'),
          icon: <PiFileDocLight />,
          onClick: handleDownload
        },
        {
          id: 'download-pdf',
          label: t('Download as PDF'),
          icon: <PiFilePdfLight />,
          onClick: handleDownloadAsPdf
        }
      );
    } else {
      items.push({
        id: 'download',
        label: t('Download'),
        icon: <PiCloudArrowDownLight />,
        onClick: handleDownload
      });
    }

    // No "Edit CSV" entry: a spreadsheet is edited in its own cells (files-18),
    // and this one never did anything - its onClick was an empty function.

    items.push({
      id: 'activity',
      label: t('Activity'),
      icon: <PiClockCounterClockwiseLight />,
      onClick: () => setShowAuditLog(true),
      divider: true
    });

    // No delete for matter files - they are managed through the matter workflow
    if (!isMatterFile) {
      items.push({
        id: 'delete',
        label: t('Delete'),
        icon: <PiTrashLight />,
        onClick: handleDelete,
        variant: 'danger',
        isDesctructive: true
      });
    }

    return items;
  };

  /*
    NO CLOSE BUTTON (files-2).

    The header used to offer three ways out of one document - Close, the back
    chevron and the breadcrumb trail. The breadcrumb is the way back: it says
    where you are going, and it lands you in the folder you came from. What is
    left here is Info (files-3) and Expand (files-5).
  */
  const getHeaderActions = () => {
    const actions: any[] = [];

    actions.push({
      id: 'info',
      label: t('Info'),
      icon: <PiInfoLight />,
      onClick: openInfoModal,
      variant: 'outline'
    });

    // Expand button (not visible in fullscreen)
    if (!isFullscreen) {
      actions.push({
        id: 'expand',
        label: t('Expand'),
        icon: <PiArrowsOutLight />,
        onClick: () => setIsFullscreen(true),
        variant: 'outline'
      });
    }

    return actions;
  };

  // Determine file type for skeleton
  const getFileTypeForSkeleton = (): 'docx' | 'pdf' | 'excel' | 'text' | 'unknown' => {
    if (!fileData) return 'unknown';
    const fileName = fileData.FileName.toLowerCase();
    const mimeType = (fileData.FileType || '').toLowerCase();

    if (mimeType.includes('pdf') || fileName.endsWith('.pdf')) return 'pdf';
    if (
      mimeType.includes('spreadsheet') ||
      mimeType.includes('sheet') ||
      mimeType.includes('excel') ||
      fileName.endsWith('.xlsx') ||
      fileName.endsWith('.csv')
    ) return 'excel';
    if (
      mimeType.includes('word') ||
      mimeType.includes('document') ||
      mimeType.includes('wordprocessingml') ||
      fileName.endsWith('.docx')
    ) return 'docx';
    if (mimeType.includes('text') || fileName.endsWith('.txt')) return 'text';
    return 'unknown';
  };

  // Get human-readable file type for display
  const getHumanReadableFileType = (): string => {
    if (!fileData) return '';
    const fileName = fileData.FileName.toLowerCase();
    const mimeType = (fileData.FileType || '').toLowerCase();

    if (mimeType.includes('pdf') || fileName.endsWith('.pdf')) return 'PDF';
    if (
      mimeType.includes('spreadsheet') ||
      mimeType.includes('sheet') ||
      mimeType.includes('excel') ||
      fileName.endsWith('.xlsx')
    ) return 'Excel';
    if (fileName.endsWith('.csv')) return 'CSV';
    if (
      mimeType.includes('word') ||
      mimeType.includes('document') ||
      mimeType.includes('wordprocessingml') ||
      fileName.endsWith('.docx')
    ) return 'Word Document';
    if (mimeType.includes('text') || fileName.endsWith('.txt')) return 'Text File';

    // Extract extension from filename
    const ext = fileName.split('.').pop()?.toUpperCase() || 'File';
    return ext;
  };

  /**
   * The document's metadata, on demand (files-3).
   *
   * Type, size, dates and who it is shared with used to sit in a row above the
   * document, competing with it for the viewport. They live here now, one
   * click away, and the name appears once - in the breadcrumb.
   */
  const openInfoModal = useCallback(() => {
    if (!fileData) return;
    const { name: formattedName, extension } = formatDocumentName(fileData.FileName);
    dispatch(showModal({
      showModal: true,
      type: 'documentInfo',
      title: t('Document info'),
      message: '',
      icon: <PiInfoLight className="w-6 h-6 text-fg-body" />,
      onConfirm: () => { },
      documentInfo: {
        name: extension ? `${formattedName}.${extension}` : formattedName,
        type: getHumanReadableFileType(),
        size: fileData.FileSize,
        createdAt: fileData.CreatedAt,
        modifiedAt: fileData.ModifiedAt,
        matterTitle: fileData.MatterInfo?.title,
        folderPath: (fileData.ParentChain || []).map((p: any) => p.name).filter(Boolean).join(' / ') || undefined,
        sharedWith: (fileData.SharedWith || []).map((s: any) => ({
          user_uuid: s.user_uuid || s.UserID || '',
          name: s.name || s.Name || [s.FirstName, s.LastName].filter(Boolean).join(' '),
          initials: (s.name || s.Name || `${s.FirstName || ''} ${s.LastName || ''}`)
            .split(' ')
            .map((n: string) => n[0])
            .filter(Boolean)
            .join('')
            .slice(0, 2)
            .toUpperCase(),
          access: s.access || s.role || s.Role || '',
          email: s.email || s.Email || null,
          avatar_url: s.avatar_url || s.ProfilePicture || null,
        })),
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, fileData, t]);

  // Map collaborators to badge format
  const collaboratorBadges: CollaboratorBadge[] = useMemo(() => {
    if (!fileData?.Collaborators) return [];
    return fileData.Collaborators.map((c: any) => ({
      user_uuid: c.user_uuid || c.UserID || '',
      name: c.name || c.Name || '',
      initials: c.initials || (c.name || '').split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase(),
      access: c.access || c.role || '',
      email: c.email || null,
      avatar_url: c.avatar_url || null
    }));
  }, [fileData?.Collaborators]);

  // ONE zoom control (files-3), rendered in the normal header and again in the
  // slim reading bar - the same state either way.
  const handleZoomChange = (next: number) => {
    setZoom(next);
    writeStoredZoom(next);
  };
  const zoomControl = <ViewerZoomControl zoom={zoom} onZoomChange={handleZoomChange} />;

  if (isLoading || !fileData) {
    return (
      <DetailFrame
        breadcrumbs={getBreadcrumbs()}
        dropdownActions={getActionItems()}
        actions={getHeaderActions()}
        onBack={onClose}
        className="flex flex-col h-full"
        additionalHeaderElements={zoomControl}
        isFullscreen={isFullscreen}
        onExitFullscreen={() => setIsFullscreen(false)}
      >
        <FileTypeSkeleton fileType={getFileTypeForSkeleton()} />
      </DetailFrame>
    );
  }

  return (
    <DetailFrame
      breadcrumbs={getBreadcrumbs()}
      dropdownActions={getActionItems()}
      actions={getHeaderActions()}
      onBack={onClose}
      className="flex flex-col h-full"
      additionalHeaderElements={zoomControl}
      isFullscreen={isFullscreen}
      onExitFullscreen={() => setIsFullscreen(false)}
      fullscreenActions={
        <>
          <ViewerZoomControl zoom={zoom} onZoomChange={handleZoomChange} compact />
          <button
            onClick={openInfoModal}
            className="flex items-center justify-center p-1.5 rounded transition-colors text-fg-body hover:bg-surface-sunken/70 hover:text-champagne"
            title={t('Info')}
            aria-label={t('Info')}
          >
            <PiInfoLight className="h-4 w-4" />
          </button>
        </>
      }
      fileInfo={fileData ? {
        name: (() => {
          const { name: formattedName, extension } = formatDocumentName(fileData.FileName);
          return extension ? `${formattedName} (${extension.toUpperCase()})` : formattedName;
        })(),
        type: getHumanReadableFileType()
      } : undefined}
      collaborators={collaboratorBadges}
    >
      {/* File Navigator */}
      {!isFullscreen && (
        <div data-tour-id="file-navigator">
        <FileNavigator
          files={allFolderFiles}
          currentFileId={currentFileId}
          onFileChange={(fileId) => {
            setCurrentFileId(fileId);
            setFileData(null);
            setIsPDF(false);
          }}
          folders={currentFolderSubFolders}
          onFolderChange={(folderId) => {
            setCurrentFolderId(folderId);
            setCurrentFileId('');
            setFileData(null);
          }}
          matterInfo={fileData?.MatterInfo}
        />
        </div>
      )}

      {/* Main content */}
      <div data-tour-id="file-content-area" className="flex-1 overflow-hidden">
        {fileData && (
          <SecureFileViewer
            fileData={{
              FileID: fileData.FileID,
              FileName: fileData.FileName,
              FileType: fileData.FileType,
              decryptedBlobUrl: fileData.decryptedBlobUrl,
              FileSize: fileData.FileSize,
              FolderID: folderID
            }}
            isLoading={isLoading}
            zoom={zoom}
            onSave={async (htmlContent, fileName) => {
              try {
                await dispatch(editDocument(fileData.FileID, fileName, htmlContent, folderID, false));
                getFileDetails(currentFileId, folderID, parentFolderID).catch(err => console.error('Error refreshing file details:', err));
              } catch (error) {
                console.error('Error saving file:', error);
              }
            }}
            onDelete={async () => {
              await handleDelete();
            }}
            onClose={() => {
              onClose();
            }}
            onShowVersionHistory={() => {
              if (fileData) {
                const { name, extension } = formatDocumentName(fileData.FileName);
                const formattedFileName = extension ? `${name} (${extension.toUpperCase()})` : name;
                dispatch(showModal({
                  showModal: true,
                  type: 'versionHistory',
                  fileId: fileData.FileID,
                  folderId: folderID || '',
                  fileName: fileData.FileName,
                  onVersionRestore: () => {
                    dispatch(hideModal());
                    getFileDetails(currentFileId, folderID, parentFolderID);
                  },
                  message: t('Version History'),
                  icon: <PiClockBold className="w-6 h-6 text-fg-body" />,
                  subMsg: formattedFileName
                }));
              }
            }}
            onDownload={handleDownload}
          />
        )}
      </div>

      <FeatureTour
        steps={fileViewerTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        disableOverlay
      />

      {/* Audit Log Side Panel */}
      {showAuditLog && fileData && (
        <ActivityDrawer onClose={() => setShowAuditLog(false)}>
          <FileAuditLog
            entityType="file"
            entityId={fileData.FileID}
            entityName={fileData.FileName}
            onClose={() => setShowAuditLog(false)}
          />
        </ActivityDrawer>
      )}
    </DetailFrame>
  );
};

export default Editor;
